import { ResponsiveBar } from '@nivo/bar';
import { ResponsiveLine } from '@nivo/line';
import { sumBy } from 'lodash';
import React, { useEffect, useMemo } from 'react';
import { DomainTypeGq } from '../../../../graphql/projects/models/DomainTypeGq';
import { KeywordFilteringGq } from '../../../../graphql/projects/models/KeywordFilteringGq';
import { projectsApiService } from '../../../../graphql/projects/projectsApiService';
import { RankAnalysisGq } from '../../../../graphql/projects/queries/analysis/useGetImprovedRankingGq';
import { analysisFilterToKwTagFilter } from '../../../DataManagementPage/logic/analysisFilterToKwTagFilter';
import { keywordFilterToParams } from '../../../DataManagementPage/logic/keywordFilterToParams';
import { ProjectTabChoices } from '../../../ProjectPage/ProjectPage';
import AnalysisChartLoading from '../../AnalysisChartLoading';
import { ChartProps } from '../../AnalysisList';
import { ColorPalette } from '../../Palette/ColorPalette';
import { Language, internationalText } from '../../internationalText';
import NotEnoughData from '../NotEnoughData';

export interface ImprovedRankingProps extends ChartProps {}

export interface ImprovedRank {
  readonly rank: string;
  readonly keywordCount: number;
  readonly searchVolume: number;
  readonly traffic: number;
}

const LineChart: React.FC<{ data: ImprovedRank[]; language: Language; palette: ColorPalette }> = ({
  data,
  language,
  palette,
}) => {
  const lineData = [
    {
      id: 1,
      data: data.map((d) => ({
        x: d.rank,
        y: d.searchVolume,
      })),
    },
  ];

  return (
    <div className="absolute top-0 h-[500px] px-5 w-full">
      <ResponsiveLine
        data={lineData}
        margin={{ top: 50, right: 65, bottom: 55, left: 75 }}
        colors={palette.primaryScale[2]}
        axisBottom={null}
        axisRight={null}
        pointColor={palette.primaryScale[2]}
        pointSize={12}
        axisLeft={{
          format: '~s',
          tickSize: 0,
          tickPadding: 24,
          tickRotation: 0,
          legend: internationalText.chart.market_potential_by_rank_improvement.vol_sum[language],
          legendPosition: 'middle',
          legendOffset: -70,
          tickValues: 10,
        }}
        lineWidth={3.5}
        curve={'monotoneX'}
        isInteractive={false}
        enableGridX={false}
        enableGridY={false}
        enablePoints={true}
        animate={false}
      />
    </div>
  );
};

const BarChart: React.FC<{
  data: ImprovedRank[];
  language: Language;
  palette: ColorPalette;
  viewSelectedData: (rank: number) => void;
}> = ({ data, language, palette, viewSelectedData }) => {
  const yMax = Math.max(...data.map((x) => x.keywordCount));
  return (
    <div className="absolute top-0 w-full h-[500px]">
      <ResponsiveBar
        onClick={(node, event) => viewSelectedData(Number(node.data['rank']))}
        data={data as any}
        padding={0}
        keys={['keywordCount']}
        maxValue={yMax + 20}
        indexBy="rank"
        enableLabel={false}
        colors={'transparent'}
        borderRadius={2}
        borderWidth={3}
        isInteractive
        borderColor={palette.tertiaryScale[2]}
        axisBottom={{
          tickSize: 5,
          tickPadding: 10,
          tickRotation: 0,
          legend: internationalText.chart.market_potential_by_rank_improvement.rank[language],
          legendPosition: 'middle',
          legendOffset: 46,
        }}
        axisLeft={null}
        tooltip={(props) => (
          <div
            className={`
            bg-white
            shadow
            p-3 rounded-md
            font-chart
            flex flex-col gap-1 items-center
          `}
          >
            <span className="font-semibold text-sm text-voxel-gray-700">
              {props.value} keywords
            </span>
            <span className="font-regular text-xs text-voxel-gray-500">Click to drill down</span>
          </div>
        )}
        axisRight={{
          format: '~s',
          tickSize: 0,
          tickPadding: 10,
          tickRotation: 0,
          legend:
            internationalText.chart.market_potential_by_rank_improvement.legend_kw_count[language],
          legendPosition: 'middle',
          legendOffset: 50,
          tickValues: 10,
        }}
        margin={{ top: 50, right: 65, bottom: 55, left: 75 }}
        enableGridX={false}
        enableGridY={false}
        animate={false}
      />
    </div>
  );
};

export const ImprovedRanking: React.FC<ImprovedRankingProps> = ({
  project,
  analysisFilterState,
  setAnalysisFilterState,
  language,
  palette,
}) => {
  // Adjust shown filters
  useEffect(
    () =>
      setAnalysisFilterState((prev) => ({
        ...prev,
        shownFields: {
          showRankSumIntervals: true,
          showTagGroupId: true,
          showTagId: true,
        },
      })),
    []
  );

  // Create url with searchParams for keyword table filter
  const viewSelectedChartData = (selectedRankValue: number) => {
    // Create filter
    const domain = project.domains.find((d) => d.type === DomainTypeGq.PROJECT);
    const kwFilter: KeywordFilteringGq = {
      projectId: project.id,
      tagFilters: analysisFilterToKwTagFilter(analysisFilterState, project),
      domainRankFilters: [
        { domainId: domain.id, minRank: selectedRankValue, maxRank: selectedRankValue },
      ],
    };

    const params = keywordFilterToParams(kwFilter);

    // Create url
    const currentUrl = new URL(window.location.href);
    const urlStart = currentUrl.href.substring(
      0,
      currentUrl.href.indexOf(`/${ProjectTabChoices.Analyze}`)
    );
    const newUrl = `${urlStart}/${ProjectTabChoices.Manage}${params}`;

    // Open in new tab
    Object.assign(document.createElement('a'), { target: '_blank', href: newUrl }).click();
  };

  // Data fetching
  const dataQuery = projectsApiService.queries.analysis.useGetImprovedRanking({
    projectId: project.id,
    tagGroupId: analysisFilterState?.tagGroupId,
    tagId: analysisFilterState?.tagId,
  });

  const dataLoading = dataQuery?.query?.loading;
  const dataResult: RankAnalysisGq[] = dataQuery?.result?.rankAnalyses ?? [];

  const maxRank = 30;
  const rankingList = Array.from({ length: maxRank }, (v, i) => i + 1);

  const rankData: ImprovedRank[] = rankingList.map((d) => ({
    rank: d.toString(),
    keywordCount: dataResult.find((x) => x.rank === d)?.keywordCount ?? 0,
    searchVolume: dataResult.find((x) => x.rank === d)?.searchVolume ?? 0,
    traffic: dataResult.find((x) => x.rank === d)?.traffic ?? 0,
  }));

  const others = {
    rank: '31+',
    keywordCount: sumBy(dataResult, (x) => (x.rank > maxRank && x.rank < 100 ? x.keywordCount : 0)),
    searchVolume: sumBy(dataResult, (x) => (x.rank > maxRank && x.rank < 100 ? x.searchVolume : 0)),
    traffic: sumBy(dataResult, (x) => (x.rank > maxRank && x.rank < 100 ? x.traffic : 0)),
  };

  const nonranking = {
    rank: '100+',
    keywordCount: sumBy(dataResult, (x) => (x.rank === 100 ? x.keywordCount : 0)),
    searchVolume: sumBy(dataResult, (x) => (x.rank === 100 ? x.searchVolume : 0)),
    traffic: sumBy(dataResult, (x) => (x.rank === 100 ? x.traffic : 0)),
  };

  const chartData = useMemo(() => {
    if (analysisFilterState?.rankSumIntervals === '2') {
      return [...rankData, others, nonranking];
    } else if (analysisFilterState?.rankSumIntervals === '1') {
      return [...rankData, others];
    } else return rankData;
  }, [analysisFilterState?.rankSumIntervals, dataResult]);

  // Render
  if (dataLoading || chartData === null) {
    return <AnalysisChartLoading />;
  }

  if (!chartData?.some((x) => x.keywordCount > 0)) {
    return <NotEnoughData />;
  }

  return (
    <div data-screenshot-id="chart-loaded" className={'mt-4 flex flex-col h-fit'}>
      <div className={'flex flex-row justify-center'}>
        <div className={'pr-4 py-1 flex flex-row items-center'}>
          <div
            className="w-5 h-1 rounded-sm border border-solid"
            style={{
              borderColor: palette.primaryScale[2],
              backgroundColor: palette.primaryScale[2],
            }}
          />
          <div className="pl-3">
            {internationalText.chart.market_potential_by_rank_improvement.legend_volume[language]}
          </div>
        </div>
        <div className={'pr-4 py-1 flex flex-row items-center'}>
          <div
            className="w-5 h-5 rounded-[3px] border-[3px] border-solid"
            style={{ borderColor: palette.tertiaryScale[2] }}
          />
          <div className="pl-3">
            {internationalText.chart.market_potential_by_rank_improvement.legend_kw_count[language]}
          </div>
        </div>
      </div>
      <div className={'w-full h-[500px] relative'}>
        <LineChart data={chartData} language={language} palette={palette} />
        <BarChart
          data={chartData}
          language={language}
          palette={palette}
          viewSelectedData={(event) => viewSelectedChartData(event)}
        />
      </div>
    </div>
  );
};

export default ImprovedRanking;
